import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { SearchStateService } from '../../shared/services/search-state.service';
import { CommonModule } from '@angular/common';
import { ProviderSearchFilters, SearchFilter } from '../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { Observable, of } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { PostGetProviderSearchRequest } from '../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-provider-search-filter',
  imports: [CommonModule, FormsModule, MatAutocompleteModule, MatCheckboxModule, MatChipsModule, MatIconModule],
  templateUrl: './provider-search-filter.component.html',
  styleUrl: './provider-search-filter.component.css'
})

export class ProviderSearchFilterComponent {
  @Input() currentLocation: { latitude: number; longitude: number } | null = null;
  @Input() patientAffiliation: string | null = null;  
  @Input() providerSearchTypeAheadOptions: string[] = [];

  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);

  providerSearchQuery$ = this.searchState.providerSearchQuery$;
  providerSearchResults$ = this.searchState.providerSearchResults$;
  providerSearchFilters$ = this.searchState.providerSearchFilters$;
  providerSortMethod$ = this.searchState.providerSortMethod$;
  currentFilters: ProviderSearchFilters | null = null;

  providerSearchResults: any[] = [];
  providerSortMethod:'distance' | 'availability' | 'name' = 'distance';
  filteredNameOrSpecialties$!: Observable<string[]>;
  filteredSpecialties$!: Observable<SearchFilter[]>;
  specialtySearchText: string = '';
  allSpecialties: SearchFilter[] = [];
  selectedSpecialties: SearchFilter[] = [];

  filteredLanguages$!: Observable<SearchFilter[]>;
  locationText: string = '';
  languageSearchText: string = '';
  allLanguages: SearchFilter[] = [];
  selectedLanguages: SearchFilter[] = [];
  additionalSelectedLanguages: SearchFilter[] = [];

  queryNameOrSpecialty: string = '';
  queryCityOrZip: string = '';

  specialtyCount: number = 0;
  languageCount: number = 0;

  locale = "en-US";
  searchPlaceholderText = "Search";
  providerLabel = "Provider Name";
  specialtyLabel = "Specialty";
  onlineSchedLabel = "Online Scheduling";
  medGroupLabel = "Medical Group";
  genderLabel = "Gender";
  langLabel = "Languages";
  acceptedInsuranceLabel = "Accepted Insurance";
  clearBtnText = "Clear all";
  applyBtnText = "Apply";

  showSpinner: boolean = false;

  constructor(private translate: TranslateService) {
    this.locale =  sessionStorage.getItem('locale') || 'en-US';
    this.translate.use(this.locale); // Default to 'en-US' if no locale is provided
  }

  ngOnInit(): void {

    //translations
    this.translate.get('SEARCH').subscribe((translated: string) => {
      this.searchPlaceholderText = translated;
    });
    
    this.translate.get('FILTERS.PROVIDER_NAME_SPECIALTY').subscribe((translated: string) => {
      this.providerLabel = translated;
    });

    this.translate.get('FILTERS.SPECIALTIES').subscribe((translated: string) => {
      this.specialtyLabel = translated;
    });

    this.translate.get('FILTERS.ACCEPTED_INSURANCE').subscribe((translated: string) => {
      this.acceptedInsuranceLabel = translated;
    });

    this.translate.get('FILTERS.ONLINE_SCHEDULING').subscribe((translated: string) => {
      this.onlineSchedLabel = translated;
    });

    this.translate.get('FILTERS.GENDER').subscribe((translated: string) => {
      this.genderLabel = translated;
    });

    this.translate.get('FILTERS.LANGUAGES').subscribe((translated: string) => {
      this.langLabel = translated;
    });

    this.translate.get('FILTERS.MEDICAL_GROUP').subscribe((translated: string) => {
      this.medGroupLabel = translated;
    });

    this.translate.get('APPLY').subscribe((translated: string) => {
      this.applyBtnText = translated;
    });

    this.translate.get('CLEAR_ALL').subscribe((translated: string) => {
      this.clearBtnText = translated;
    });
    
    this.providerSearchQuery$.subscribe(query => {
      if (query) {
        this.queryNameOrSpecialty = query.nameOrSpecialty; // ProviderName "search results" on top of screen
        this.queryCityOrZip = query.cityOrZip; // update the location filter input value 
      }
    });

    this.providerSearchResults$.subscribe(results => {
      this.providerSearchResults = results ?? [];
    });

    this.providerSortMethod$.subscribe(sortBy => {
      this.providerSortMethod = sortBy;
    });

    this.providerSearchFilters$.subscribe((filters: ProviderSearchFilters | null) => {
      console.log('Filters in Component:', filters);
      this.currentFilters = filters;

      // Calculate specialty count and initialize specialty list
      if (filters && filters.specialtyList) {
        this.allSpecialties = filters.specialtyList;
        this.selectedSpecialties = filters.specialtyList.filter(option => option.isSelected == true);
        this.specialtyCount = this.selectedSpecialties.length;
        // Initialize with unselected specialties only
        this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));
      }

      // Initialize language list
      if (filters && filters.languageList) {
        this.allLanguages = filters.languageList;
        this.selectedLanguages = filters.languageList.filter(option => option.isSelected == true);
        this.languageCount = this.selectedLanguages.length;
        // Initialize with languages not in the displayed list (first 5 + additional)
        const displayedLanguages = this.getDisplayedLanguages();
        this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
      }
    });  
  }

  filterNameOrSpecialties(searchText: string): void {
    if (!searchText || searchText.length < 3) {
      this.filteredNameOrSpecialties$ = of([]);
      return;
    }

    if (!this.providerSearchTypeAheadOptions || !Array.isArray(this.providerSearchTypeAheadOptions)) {
      this.filteredNameOrSpecialties$ = of([]);
      return;
    }

    const normalizeString = (str: string) => str.toLowerCase().replace(/[^a-z0-9\s]/gi, '');
    const filterValue = normalizeString(searchText);
    
    const filtered = this.providerSearchTypeAheadOptions.filter(option => 
      normalizeString(option).includes(filterValue)
    );
    
    this.filteredNameOrSpecialties$ = of(filtered);
  }

  filterSpecialties(searchText: string): void {
    if (!searchText || searchText.trim() === '') {
      // Filter out already selected specialties
      this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));
    } else {
      const filterValue = searchText.toLowerCase();
      // Filter by search text AND exclude already selected specialties
      const filtered = this.allSpecialties.filter(specialty => 
        specialty.value.toLowerCase().includes(filterValue) && !specialty.isSelected
      );
      this.filteredSpecialties$ = of(filtered);
    }
  }

  private findScrollableParent(element: Element): Element | null {
      let parent = element.parentElement;
      while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    }

  onSpecialtySelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    const specialty = this.allSpecialties.find(s => s.value === selectedValue);
    
    if (specialty && !specialty.isSelected) {
      specialty.isSelected = true;
      this.selectedSpecialties = this.allSpecialties.filter(s => s.isSelected);
      this.specialtyCount = this.selectedSpecialties.length;
      console.log('Specialty added:', specialty);
      console.log('COUNT:', this.specialtyCount);
      console.log('Current selected specialties:', this.selectedSpecialties);
      console.log('Current Filters:', this.currentFilters);
    }
    
    // Clear the search text after selection
    // use setTimeout for Material Autocomplete
    setTimeout(() => {
      this.specialtySearchText = '';
      // Filter out selected specialties from the list
      this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));
    }, 0);
  }

  removeSpecialty(specialty: SearchFilter): void {
    specialty.isSelected = false;
    this.selectedSpecialties = this.allSpecialties.filter(s => s.isSelected);
    this.specialtyCount = this.selectedSpecialties.length;
    
    // Refresh the filtered list to include the removed specialty
    this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));
    
    console.log('Specialty removed:', specialty);
    console.log('COUNT:', this.specialtyCount);
    console.log('Current selected specialties:', this.selectedSpecialties);
    console.log('Current Filters:', this.currentFilters);
  }

  filterLanguages(searchText: string): void {
    // Get all languages that are currently displayed as checkboxes
    const displayedLanguages = this.getDisplayedLanguages();
    
    if (!searchText || searchText.trim() === '') {
      // Filter out all displayed languages
      this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
    } else {
      const filterValue = searchText.toLowerCase();
      // Filter by search text AND exclude all displayed languages
      const filtered = this.allLanguages.filter(language => 
        language.value.toLowerCase().includes(filterValue) && !displayedLanguages.includes(language)
      );
      this.filteredLanguages$ = of(filtered);
    }
  }

  onLanguageSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    const language = this.allLanguages.find(l => l.value === selectedValue);
    
    if (language && !language.isSelected) {
      language.isSelected = true;
      this.selectedLanguages = this.allLanguages.filter(l => l.isSelected);
      this.languageCount = this.selectedLanguages.length;
      
      // Track if this language is not in the first 5
      const languageIndex = this.allLanguages.indexOf(language);
      if (languageIndex >= 5 && !this.additionalSelectedLanguages.includes(language)) {
        this.additionalSelectedLanguages.push(language);
      }
      
      console.log('Language added:', language);
      console.log('COUNT:', this.languageCount);
      console.log('Current selected languages:', this.selectedLanguages);
    }
    
    // Clear the search text after selection
    setTimeout(() => {
      this.languageSearchText = '';
      // Filter out all displayed languages from the list
      const displayedLanguages = this.getDisplayedLanguages();
      this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
    }, 0);
  }

  removeLanguage(language: SearchFilter): void {
    language.isSelected = false;
    this.selectedLanguages = this.allLanguages.filter(l => l.isSelected);
    this.languageCount = this.selectedLanguages.length;
    
    // Remove from additional languages if present
    const index = this.additionalSelectedLanguages.indexOf(language);
    if (index > -1) {
      this.additionalSelectedLanguages.splice(index, 1);
    }
    
    // Refresh the filtered list to exclude all displayed languages
    const displayedLanguages = this.getDisplayedLanguages();
    this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
    
    console.log('Language removed:', language);
    console.log('COUNT:', this.languageCount);
    console.log('Current selected languages:', this.selectedLanguages);
  }

  getDisplayedLanguages(): SearchFilter[] {
    const firstFive = this.allLanguages.slice(0, 5);
    const result = [...firstFive];
    
    // Add additional selected languages that are not in the first 5
    this.additionalSelectedLanguages.forEach(lang => {
      if (!result.includes(lang)) {
        result.push(lang);
      }
    });
    
    return result;
  }

  onGenderChange(option: SearchFilter) {
    console.log('Box changed:', option);
    if (this.currentFilters?.genderList) {
      // Deselect all other gender options
      this.currentFilters.genderList.forEach(g => {
        if (g.value != option.value) {
          g.isSelected = false;
        }
      });

      console.log('Gender changed:', option);
      console.log('Current Filters:', this.currentFilters);
    }
  }

  onFilterChange(filterType: string, option: any) {
    console.log(`${filterType} filter changed (not applied yet):`, option);
    // console.log('Current Filters:', this.currentFilters);    
  }

  onApplyFilters() {
    console.log('Applying Filters:', this.currentFilters);

    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.searchState.clearCurrentProviderDetailsLocation();
    this.searchState.clearCurrentProviderDetails();
    this.searchState.clearCurrentLocationDetailsProvider();
    this.searchState.clearCurrentLocationDetails();
    this.searchState.setShowSpinner(true);

    // filter current ProviderSearchResults based on currentFilters
    // Filter results based on current filters
    let filteredResults = [...this.providerSearchResults];

    var request = new PostGetProviderSearchRequest();

    request.nameOrSpecialty = this.queryNameOrSpecialty; // sourced from the input text field
    request.cityOrZip = this.queryCityOrZip; // sourced from the ProviderResults component via stateService
    request.distanceInMiles = this.currentFilters?.distanceInMiles ?? 15;

    request.specialties = this.currentFilters?.specialtyList?.filter(s => s.isSelected).map(s => s.value).join(', ');
    request.onlineScheduling = this.currentFilters?.onlineSchedulingList?.some(o => o.isSelected) ? true : undefined;
    request.affiliations = this.currentFilters?.affiliationList?.filter(a => a.isSelected).map(a => a.value).join(', ');
        
    request.gender = this.currentFilters?.genderList?.find(g => g.isSelected)?.value ?? '';
    request.languages = this.currentFilters?.languageList?.filter(l => l.isSelected).map(l => l.value).join(', ');

    // only set the lattitude and longitude if a cityOrZip value is not provided
    if ((request.cityOrZip == null || request.cityOrZip == "") && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude ) {
      request.latitude = this.currentLocation.latitude;
      request.longitude = this.currentLocation.longitude;
    }
    
    this.mcAppGateway.PostGetProviderSearch(request).subscribe({
      next: (response) => {
        var sortedResponse = this.searchState.sortProviderSearchResults(response || [], this.providerSortMethod);
        console.log('Provider Search Response (after applying filters):', sortedResponse);
        this.searchState.setProviderSearchResults(sortedResponse);
        this.searchState.setProviderSearchFilters(this.currentFilters!);
        this.searchState.setProviderSearchQuery({
          nameOrSpecialty: this.queryNameOrSpecialty, // update display " '<query>' search results" on top of screen
          cityOrZip: this.queryCityOrZip,
        });

        this.searchState.setShowSpinner(false);
      },
      error: (error) => {
        console.error('An error occurred while searching for providers with filters: ', error);
        alert('An error occurred while searching for providers with filters: ' + error.message);

        this.searchState.setShowSpinner(false);
      },
    });

    // set the results based on the api response
    // set the provider filtes her as well
    // this.searchState.setProviderSearchResults(response);
    this.searchState.setProviderSearchFilters(this.currentFilters!);
  }

  onClearAllFilters(): void {
    // Clear provider name text
    this.queryNameOrSpecialty = '';
    
    // Clear specialty search text
    this.specialtySearchText = '';
    
    // Unselect all specialties
    if (this.allSpecialties && this.allSpecialties.length > 0) {
      this.allSpecialties.forEach(specialty => {
        specialty.isSelected = false;
      });
    }
    this.selectedSpecialties = [];
    this.specialtyCount = 0;
    
    // Reset filtered specialties list
    this.filteredSpecialties$ = of(this.allSpecialties);
    
    // Clear language search text
    this.languageSearchText = '';
    
    // Unselect all languages
    if (this.allLanguages && this.allLanguages.length > 0) {
      this.allLanguages.forEach(language => {
        language.isSelected = false;
      });
    }
    this.selectedLanguages = [];
    this.additionalSelectedLanguages = [];
    this.languageCount = 0;
    
    // Reset filtered languages list
    const displayedLanguages = this.getDisplayedLanguages();
    this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
    
    // Unselect all online scheduling checkboxes
    if (this.currentFilters?.onlineSchedulingList) {
      this.currentFilters.onlineSchedulingList.forEach(option => {
        option.isSelected = false;
      });
    }
    
    // Unselect all affiliation checkboxes
    if (this.currentFilters?.affiliationList) {
      this.currentFilters.affiliationList.forEach(option => {
        option.isSelected = false;
      });
    }
    
    // Unselect all gender checkboxes
    if (this.currentFilters?.genderList) {
      this.currentFilters.genderList.forEach(option => {
        option.isSelected = false;
      });
    }
    
    console.log('All filters cleared');
  }
}
